import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import { query, doc } from "firebase/firestore";
import {
  Timestamp,
  IOrder,
  IOrderTemplate,
  IRouteItem,
  IOrderProduct,
  ICustomer,
} from "../types";
import {
  IRecurrenceRule,
  IRecurrenceState,
} from "../types/order/IOrderTemplate";
import { OrderStatus } from "../types/order/IOrder";
import { IProductStatus } from "../types/OrderProduct";
import Order from "./Order";
import Customer from "./Customer";
import { getApiToken } from "../data/api/token";
import {
  createOrderTemplate as createOrderTemplateApi,
  updateOrderTemplate as updateOrderTemplateApi,
  deleteOrderTemplate as deleteOrderTemplateApi,
} from "../data/api/orderTemplates";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

/**
 * Reusable order template model. Stores an order snapshot with all static
 * dates stripped (see {@link OrderTemplate.fromOrder}). Bridges to the order
 * wizard through {@link OrderTemplate.toOrder}.
 */
class OrderTemplate implements IOrderTemplate {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  customer: ICustomer;
  customerId: string;
  route: IRouteItem[];
  cargo: IOrderProduct[];
  driverId?: string;
  comment: string;
  projectNumber?: string;
  orderPricing?: IOrder["orderPricing"];
  schemaVersion: number;
  createdAt: Timestamp | null;
  createdBy: string | null;
  lastUpdated: Timestamp | null;
  recurrence?: IRecurrenceRule;
  stopDayOffsets?: number[];
  generationState?: IRecurrenceState;

  static collectionName = "order_templates";
  static currentSchemaVersion = 1;

  static converter = {
    toFirestore(template: OrderTemplate) {
      return template.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IOrderTemplate;
      return new OrderTemplate({ ...data, id: snapshot.id });
    },
  };

  static createId() {
    return firestore.collection(OrderTemplate.collectionName).doc().id;
  }

  constructor({
    id,
    workspaceId,
    name,
    description,
    customer,
    customerId,
    route,
    cargo,
    driverId,
    comment,
    projectNumber,
    orderPricing,
    schemaVersion,
    createdAt,
    createdBy,
    lastUpdated,
    recurrence,
    stopDayOffsets,
    generationState,
  }: IOrderTemplate) {
    this.id = id || "";
    this.workspaceId = workspaceId || "";
    this.name = name || "";
    this.description = description || "";
    this.customer = customer || (Customer.default() as ICustomer);
    this.customerId = customerId || "";
    this.route = route || [];
    this.cargo = cargo || [];
    this.driverId = driverId || undefined;
    this.comment = comment || "";
    this.projectNumber = projectNumber || undefined;
    this.orderPricing = orderPricing || undefined;
    this.schemaVersion = schemaVersion || OrderTemplate.currentSchemaVersion;
    this.createdAt = createdAt || null;
    this.createdBy = createdBy || null;
    this.lastUpdated = lastUpdated || null;
    this.recurrence = recurrence || undefined;
    this.stopDayOffsets = stopDayOffsets || undefined;
    this.generationState = generationState || undefined;
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IOrderTemplate>) {
    return Object.assign(this, updates);
  }

  /** Serialized payload for persistence. Route stop dates are always null. */
  data() {
    return {
      id: this.id || "",
      workspaceId: this.workspaceId || "",
      name: this.name || "",
      description: this.description || "",
      customer: this.customer ?? null,
      customerId: this.customerId || "",
      route: (this.route ?? []).map((stop) => ({
        ...stop,
        stopDate: null,
      })),
      cargo: this.cargo ?? [],
      driverId: this.driverId ?? null,
      comment: this.comment ?? "",
      projectNumber: this.projectNumber ?? null,
      orderPricing: this.orderPricing ?? null,
      schemaVersion: this.schemaVersion ?? OrderTemplate.currentSchemaVersion,
      // `generationState` is scheduler-managed and intentionally omitted from
      // the write payload; the backend preserves its server-side value.
      recurrence: this.recurrence ?? null,
      stopDayOffsets: this.stopDayOffsets ?? null,
      createdAt: this.createdAt ?? null,
      createdBy: this.createdBy ?? null,
      lastUpdated: this.lastUpdated ?? null,
    };
  }

  static default() {
    return new OrderTemplate({
      id: "",
      workspaceId: "",
      name: "",
      description: "",
      customer: Customer.default() as ICustomer,
      customerId: "",
      route: [],
      cargo: [],
      driverId: undefined,
      comment: "",
      projectNumber: undefined,
      orderPricing: undefined,
      schemaVersion: OrderTemplate.currentSchemaVersion,
      createdAt: null,
      createdBy: null,
      lastUpdated: null,
      recurrence: undefined,
      stopDayOffsets: undefined,
      generationState: undefined,
    });
  }

  static isNew(data: IOrderTemplate): boolean {
    return data.id.length === 0;
  }

  static get = (templateId: string) => {
    return doc(
      firestore
        .collection(OrderTemplate.collectionName)
        .withConverter(OrderTemplate.converter),
      templateId,
    );
  };

  static list = (workspaceId: string) => {
    return query(
      firestore
        .collection(OrderTemplate.collectionName)
        .where("workspaceId", "==", workspaceId)
        .withConverter(OrderTemplate.converter),
    );
  };

  static create = async (
    workspaceId: string,
    data: IOrderTemplate,
  ): Promise<OrderTemplate | null> => {
    const newTemplate = new OrderTemplate({
      ...data,
      workspaceId,
      id: "",
    });
    try {
      const token = await getApiToken();
      const created = await createOrderTemplateApi(
        token,
        workspaceId,
        newTemplate.data(),
      );
      return new OrderTemplate(created);
    } catch (e) {
      console.warn("Create order template failed with error: ", e);
      return null;
    }
  };

  static update = async (
    template: OrderTemplate,
    updates: Partial<IOrderTemplate>,
  ): Promise<OrderTemplate> => {
    const updateType =
      (template.clone && template.clone()) ||
      new OrderTemplate({ ...template });

    updateType.setData({ ...updates });

    const token = await getApiToken();
    const updated = await updateOrderTemplateApi(
      token,
      updateType.workspaceId,
      template.id,
      updateType.data(),
    );

    return new OrderTemplate(updated);
  };

  static remove = async (template: OrderTemplate) => {
    const token = await getApiToken();
    return await deleteOrderTemplateApi(
      token,
      template.workspaceId,
      template.id,
    );
  };

  /* --------------------------- sanitization ----------------------------- */

  /**
   * Reset a route stop's execution/receipt fields. A template (and any order it
   * spawns) is a fresh *planning* snapshot: it must never carry a previous
   * fulfilment's status, signature or delivery note copied from a source order.
   */
  static resetStopExecution = (stop: IRouteItem): IRouteItem => ({
    ...stop,
    status: OrderStatus.New,
    signatureUrl: undefined,
    signedBy: undefined,
    deliveryComment: undefined,
  });

  /** Reset a cargo item's execution/receipt fields (see {@link resetStopExecution}). */
  static resetCargoExecution = (cargo: IOrderProduct): IOrderProduct => ({
    ...cargo,
    status: IProductStatus.New,
    deliveredAt: null,
    deliveredQuantity: 0,
    pickedUpAt: null,
    pickedUpQuantity: 0,
    signatureUrl: undefined,
    signedBy: undefined,
    comment: undefined,
    lastUpdated: null,
  });

  /* --------------------------- wizard bridges --------------------------- */

  /**
   * Whole-day difference (>= 0) between two dates. Used to derive/apply the
   * relative day offset structure of a template's route.
   */
  static dayDiff = (base: Timestamp, other: Timestamp): number => {
    const diff = Timestamp.toMoment(other)
      .startOf("day")
      .diff(Timestamp.toMoment(base).startOf("day"), "days");
    return Number.isFinite(diff) ? Math.max(0, diff) : 0;
  };

  /**
   * Derive per-stop day offsets from an order's stop dates, relative to the
   * first stop (day 0). Preserves multi-day trip structure so recurrence can
   * reconstruct correct dates for each generated occurrence.
   */
  static offsetsFromRoute = (route: IRouteItem[]): number[] => {
    if (!route?.length) return [];
    const base = route[0]?.stopDate;
    if (!base) return route.map(() => 0);
    return route.map((stop) =>
      stop.stopDate ? OrderTemplate.dayDiff(base, stop.stopDate) : 0,
    );
  };

  /**
   * Build a template from an in-progress / existing order, stripping every
   * route stop date so the template carries no static dates. The trip's day
   * structure is preserved via {@link stopDayOffsets}.
   */
  static fromOrder = (
    order: IOrder,
    name: string,
    description?: string,
  ): OrderTemplate => {
    const stopDayOffsets = OrderTemplate.offsetsFromRoute(order.route ?? []);
    const route = (order.route ?? []).map((stop) => ({
      // Drop any prior fulfilment history from the source order...
      ...OrderTemplate.resetStopExecution(stop),
      // ...and never carry a static date.
      stopDate: null as unknown as Timestamp,
    }));
    const cargo = (order.cargo ?? []).map(OrderTemplate.resetCargoExecution);

    return new OrderTemplate({
      id: "",
      workspaceId: order.workspaceId ?? "",
      name,
      description: description ?? "",
      customer: order.customer,
      customerId: order.customerId ?? "",
      route,
      cargo,
      driverId: order.driverId,
      comment: order.comment ?? "",
      projectNumber: order.projectNumber,
      orderPricing: order.orderPricing,
      schemaVersion: OrderTemplate.currentSchemaVersion,
      createdAt: null,
      createdBy: null,
      lastUpdated: null,
      stopDayOffsets,
    });
  };

  /**
   * Convert a template into a fresh draft Order for the create-order wizard.
   * Route stop dates are reconstructed from {@link stopDayOffsets} relative to
   * today (day 0 = today, +N preserves multi-day trips), so the wizard renders
   * the correct day structure; the user re-picks the base date before creating
   * the order. Values flow through the Order constructor so any missing/new
   * fields receive sensible defaults (keeps older templates forward-compatible).
   */
  static toOrder = (template: IOrderTemplate): Order => {
    const base = Timestamp.toMoment(Timestamp.now()).startOf("day");
    const offsets = template.stopDayOffsets ?? [];
    const route = (template.route ?? []).map((stop, index) => ({
      // Defensively strip execution history in case an older template still
      // carries it (server sanitization now prevents this on save).
      ...OrderTemplate.resetStopExecution(stop),
      // Guarantee a defined pickup/delivery designation so the wizard renders a
      // valid selection. Preserve any stored value; otherwise default to the
      // wizard's own rule (first stop is a pickup, later stops are deliveries).
      isPickup: typeof stop.isPickup === "boolean" ? stop.isPickup : index === 0,
      stopDate:
        stop.stopDate ??
        Timestamp.fromDate(
          base
            .clone()
            .add(offsets[index] ?? 0, "days")
            .toDate(),
        ),
    }));
    const cargo = (template.cargo ?? []).map(OrderTemplate.resetCargoExecution);

    return new Order({
      ...Order.default(),
      customer: template.customer ?? (Customer.default() as ICustomer),
      customerId: template.customerId ?? "",
      route,
      cargo,
      driverId: template.driverId,
      comment: template.comment ?? "",
      projectNumber: template.projectNumber,
      orderPricing: template.orderPricing,
      status: OrderStatus.Draft,
      id: "",
    } as IOrder);
  };
}

export default OrderTemplate;
